import { isAuthenticated } from '../auth/authStorage';
import { listUserSettings, upsertUserSetting } from './userSettingsApi';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  normalizeNotificationPreferences,
  serializeNotificationPreferencesForApi,
} from './notificationPreferences';

const SETTINGS_KEY = 'app.settings';
export const APP_SETTINGS_CHANGED_EVENT = 'ysh:app-settings-changed';

/** Klucze API (snake_case) ↔ pola frontendu (camelCase). */
export const APP_SETTING_API_KEYS = {
  ialaRegion: 'iala_region',
  theme: 'theme',
  units: 'units',
  notifications: 'notifications',
};

const API_KEY_TO_FIELD = Object.fromEntries(
  Object.entries(APP_SETTING_API_KEYS).map(([field, apiKey]) => [apiKey, field]),
);

const DEFAULT_SETTINGS = {
  ialaRegion: 'A',
  notifications: { ...DEFAULT_NOTIFICATION_PREFERENCES },
};

let inflightSyncPromise = null;

function getLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizeSettingValue(field, value) {
  if (field === 'ialaRegion') {
    return value === 'B' ? 'B' : 'A';
  }
  if (field === 'notifications') {
    return normalizeNotificationPreferences(value);
  }
  return value;
}

function serializeSettingValueForApi(field, value) {
  if (field === 'notifications') {
    return serializeNotificationPreferencesForApi(value);
  }
  return value;
}

function readStoredSettings() {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(SETTINGS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredSettings(settings) {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // ignore quota / private mode
  }
}

function dispatchSettingsChanged(settings) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(APP_SETTINGS_CHANGED_EVENT, { detail: { ...settings } }),
  );
}

function mergeWithDefaults(partial) {
  const merged = {
    ialaRegion: DEFAULT_SETTINGS.ialaRegion,
    notifications: { ...DEFAULT_NOTIFICATION_PREFERENCES },
  };
  if (!partial || typeof partial !== 'object') return merged;

  if (partial.ialaRegion === 'A' || partial.ialaRegion === 'B') {
    merged.ialaRegion = partial.ialaRegion;
  }

  merged.notifications = normalizeNotificationPreferences(partial.notifications);

  return merged;
}

function mapServerItemsToApp(serverItems) {
  const merged = { ...DEFAULT_SETTINGS };
  if (!Array.isArray(serverItems)) return mergeWithDefaults(merged);

  for (const item of serverItems) {
    const field = API_KEY_TO_FIELD[item?.key];
    if (!field) continue;
    merged[field] = normalizeSettingValue(field, item.value);
  }

  return mergeWithDefaults(merged);
}

export function loadAppSettings() {
  return mergeWithDefaults(readStoredSettings());
}

export function loadNotificationSettings() {
  return loadAppSettings().notifications;
}

export function saveAppSettings(settings) {
  const next = mergeWithDefaults(settings);
  writeStoredSettings(next);
  dispatchSettingsChanged(next);
  return next;
}

export async function syncAppSettingsFromServer() {
  if (!isAuthenticated()) {
    return loadAppSettings();
  }

  if (inflightSyncPromise) {
    return inflightSyncPromise;
  }

  const promise = (async () => {
    try {
      const response = await listUserSettings();
      const next = mapServerItemsToApp(response?.data);
      writeStoredSettings(next);
      dispatchSettingsChanged(next);
      return next;
    } catch (error) {
      console.warn('[appSettingsStorage] syncAppSettingsFromServer: nie udało się pobrać ustawień', {
        status: error?.response?.status,
        message: error?.message,
      });
      return loadAppSettings();
    }
  })();

  const wrapped = promise.finally(() => {
    if (inflightSyncPromise === wrapped) inflightSyncPromise = null;
  });
  inflightSyncPromise = wrapped;

  return wrapped;
}

export async function updateAppSetting(field, value) {
  const normalizedValue = normalizeSettingValue(field, value);
  const apiKey = APP_SETTING_API_KEYS[field];

  const next = saveAppSettings({
    ...loadAppSettings(),
    [field]: normalizedValue,
  });

  if (!isAuthenticated() || !apiKey) {
    return next;
  }

  try {
    await upsertUserSetting(apiKey, serializeSettingValueForApi(field, normalizedValue));
  } catch (error) {
    console.warn('[appSettingsStorage] updateAppSetting: zapis na serwerze nie powiódł się', {
      field,
      apiKey,
      status: error?.response?.status,
      message: error?.message,
    });
  }

  return next;
}

export async function updateNotificationPreference(preferenceKey, enabled) {
  const current = loadAppSettings();
  const notifications = {
    ...current.notifications,
    [preferenceKey]: Boolean(enabled),
  };
  return updateAppSetting('notifications', notifications);
}
